export const SENSITIVE_KEYS = new Set([
  'accessToken',
  'accessTokens',
  'anonymousApiKey',
  'apiKey',
  'apiKeyId',
  'apiKeys',
  'client_secret',
  'clientSecret',
  'code',
  'config',
  'hostedInvoiceURL',
  'id_token',
  'idToken',
  'inviteToken',
  'invoicePDF',
  'jwk',
  'password',
  'paymentMethod',
  'photoUrl',
  'refresh_token',
  'refreshToken',
  'requestPayload',
  'resetToken',
  'resource_args',
  'secret',
  'secretApiKey',
  'secretValue',
  'sessionToken',
  'signingSecret',
  'stripePaymentMethodID',
  'templateVariables',
  'token',
  'urlEndpoint',
  'webhookUrl',
  'webhookURL',
]);

export const SENSITIVE_URL_PARAMS = new Set([
  // Report magic-link share token (`?accessToken=…`) — lands in the URL bar and
  // is captured on every RUM event via `event.view.url`.
  'accesstoken',
  'apikey',
  'code',
  'invited',
  'invitekey',
  'jwt',
  'sig',
  'signature',
  'state',
  'token',
]);

export const SENSITIVE_URL_PARAM_PREFIXES = ['x-amz-', 'x-goog-'];

/**
 * isSensitiveUrlParam reports whether a query parameter name, ignoring
 * case, names a credential or cloud-signature field that must not leave the
 * browser.
 */
export function isSensitiveUrlParam(name: string): boolean {
  const lower = name.toLowerCase();
  return (
    SENSITIVE_URL_PARAMS.has(lower) ||
    SENSITIVE_URL_PARAM_PREFIXES.some(prefix => lower.startsWith(prefix))
  );
}

export const REDACTED = '[REDACTED]';

type RedactResult = {value: unknown; changed: boolean};

/**
 * Recursively redacts SENSITIVE_KEYS matches at any nesting depth (objects and
 * arrays). Copy-on-write: a level is only shallow-copied once a redaction happens at
 * or below it, so the common no-op case (this runs on every RUM event) allocates
 * nothing and returns the original reference, letting callers cheaply detect no-ops.
 */
export function redactValue(value: unknown): RedactResult {
  if (Array.isArray(value)) {
    // `next` stays null until an item actually changes; null means "no copy made,
    // nothing redacted".
    let next: unknown[] | null = null;
    // Note: do not use .map here to avoid creating a new array
    for (let i = 0; i < value.length; i++) {
      const result = redactValue(value[i]);
      if (result.changed) {
        // First change at this level: shallow-copy the array once, then patch
        // only the changed slots. Unchanged items keep their original references.
        if (next === null) {
          next = [...value];
        }
        next[i] = result.value;
      }
    }
    return next ? {value: next, changed: true} : {value, changed: false};
  }

  if (value !== null && typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    // Same copy-on-write pattern as the array branch above.
    let next: Record<string, unknown> | null = null;
    // for...in instead of Object.entries: entries would allocate an array (plus a
    // [key, value] pair per key) on every call, and this runs on every RUM event.
    // No own-property guard: inputs are JSON.parse output / plain context objects,
    // which never have enumerable inherited keys.
    for (const key in obj) {
      let replacement: unknown;
      if (SENSITIVE_KEYS.has(key)) {
        replacement = REDACTED;
      } else {
        const result = redactValue(obj[key]);
        if (!result.changed) {
          // Nothing redacted under this key — skip before the copy step below.
          continue;
        }
        replacement = result.value;
      }
      // Only reached when this key needs patching: either the key is sensitive,
      // or a redaction happened deeper inside its value. On the first such key,
      // shallow-copy the object once; later ones just patch the copy.
      if (next === null) {
        next = {...obj};
      }
      next[key] = replacement;
    }
    return next ? {value: next, changed: true} : {value, changed: false};
  }

  return {value, changed: false};
}

/**
 * Parses a JSON string, redacts any keys that match SENSITIVE_KEYS at any
 * nesting depth, and returns the re-serialized string. Returns the original
 * if nothing was redacted. Returns REDACTED if the JSON is malformed.
 */
export function redactJsonField(raw: string | undefined): string | undefined {
  if (!raw) {
    return raw;
  }
  try {
    const parsed = JSON.parse(raw);
    const {value, changed} = redactValue(parsed);
    return changed ? JSON.stringify(value) : raw;
  } catch {
    return REDACTED;
  }
}

/**
 * Like redactJsonField but targets `.variables` inside a JSON payload
 * (the shape sent by GraphQL clients: `{query, variables}`).
 * Non-JSON payloads (raw query strings) are returned unchanged.
 */
export function redactPayloadField(
  raw: string | undefined
): string | undefined {
  if (!raw) {
    return raw;
  }
  try {
    const parsed = JSON.parse(raw);
    if (!parsed.variables) {
      return raw;
    }
    const {value, changed} = redactValue(parsed.variables);
    if (!changed) {
      return raw;
    }
    parsed.variables = value;
    return JSON.stringify(parsed);
  } catch {
    return raw;
  }
}
